import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import ProductCard from '../../Components/ProductCard';
import ProductOptions from './ProductOptions';
import Navbar from '../Components/Navbar'
import Footer from '../../Components/Footer';
import PageHeroBanner from '../Components/PageHeroBanner';
import './ProductPage.css';
import CustomizedCake from '../../Components/CustomizedCake';
import { fetchProductsByCategoryName } from '../../data/catalog';
import { STATIC_IMAGES } from '../../data/staticData';
import { useCatalog } from '../../Components/CatalogContext';
import { SORT_OPTIONS, availableBuckets, applyCatalogFilters } from '../../data/catalogFilters';
import './CatalogFilters.css';

function ProductPage() {
    const [sortBy, setSortBy] = useState('featured');
    const [priceBucket, setPriceBucket] = useState('all');
    const navigate = useNavigate();
    const { categoryName } = useParams();
    const { categories } = useCatalog();

    const processImageUrl = (imageUrl) => {
        if (!imageUrl) return STATIC_IMAGES.product;
        if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
        if (imageUrl.startsWith('/')) return imageUrl;
        return `/${imageUrl}`;
    };

    const getDefaultVariant = (cat) => {
        const c = cat?.toLowerCase() || '';
        if (c.includes('cake')) return { weight: '1kg', price: 800 };
        if (c.includes('cookie')) return { weight: 'Box of 6', price: 400 };
        return { weight: '1kg', price: 800 };
    };

    const [{ products, category, loading, error }, setState] = useState({
        products: [], category: null, loading: true, error: null,
    });

    useEffect(() => {
        let active = true;
        if (!categoryName) { setState({ products: [], category: null, loading: false, error: null }); return; }
        setState(s => ({ ...s, loading: true }));
        (async () => {
            const raw = await fetchProductsByCategoryName(categoryName);
            if (!active) return;
            const transformedProducts = raw.map(product => {
                const defaultVariant = getDefaultVariant(product.Category?.Name);
                return {
                    id: product._id,
                    name: product.productName,
                    tagline: product.Tagline,
                    description: product.ProductDesc,
                    price: product.variants?.length > 0 ? product.variants[0].price : defaultVariant.price,
                    weight: product.variants?.length > 0 ? product.variants[0].weight : defaultVariant.weight,
                    image: processImageUrl(product.Images?.[0] || null),
                    ingredients: product.Ingredients,
                    issoldout: product.isSoldOut
                };
            });
            const cat = raw[0]?.Category ? { name: raw[0].Category.Name, description: '' } : null;
            setState({ products: transformedProducts, category: cat, loading: false, error: null });
        })();
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryName]);

    const handleBackClick = () => {
        navigate('/');
    };

    const categoryContent = {
        chai: { title: "THE SOUL COLLECTION", paragraph1: "", paragraph2: "Brewed karak chai, Sticky Soul concentrate balls, and the Chai Pe Charcha monthly box. Gut-friendly spices, authentic family recipe from Mumbai. Just add hot water - instant warmth." },
        snacks: { title: "THE CRUNCH COLLECTION", paragraph1: "", paragraph2: "Roasted lotus seeds (Pop & Bloom), protein trail mixes, brain-boosting and gut-friendly snacks. Ancient crunch, modern munch. High protein, gluten-free, no guilt." },
        drinks: { title: "THE REFRESH COLLECTION", paragraph1: "", paragraph2: "Berry Banger superfruit wellness shots. No added sugar. Antioxidant-rich, vitamin C boost. Bang. Boom. Berry." },
        'subscription boxes': { title: "THE RITUAL COLLECTION", paragraph1: "", paragraph2: "The Weekly Ritual and The Discovery Box. Fresh wellness delivered. Your week sorted - chai, rotating snacks, Berry Banger, and something special every time." },
        'retail & merch': { title: "THE LIFESTYLE COLLECTION", paragraph1: "", paragraph2: "Apron, cap, Heritage Tiffin, Spice Wheel masala dabba, Ritual Jar, Soul Box gift sets, and Chai & Connect wellness workshops. Wear your wellness. Pack with love." }
    };

    // Same banner backdrop the /allproduct page uses, for a consistent look.
    const CATEGORY_BANNER_IMAGE = '/bgimage.jpeg';

    const currentContent = categoryContent[categoryName] || {
        title: category?.name?.toUpperCase() || 'PRODUCTS',
        paragraph1: "",
        paragraph2: category?.description || "Discover our collection of premium products made with the finest ingredients and traditional recipes."
    };

    // Admin-managed collection meta (from MongoDB via the catalog) wins over the hardcoded fallback.
    const catMeta = (categories || []).find(
        (c) => c && c.Name && c.Name.toLowerCase() === (categoryName || '').toLowerCase()
    );
    const heroEyebrow = catMeta?.Subtitle || 'Fresh · Handcrafted · Wellness';
    const heroHeading = catMeta?.CollectionTitle || currentContent.title;
    const heroDescription = catMeta?.Description || currentContent.paragraph2;

    // Price / sort / availability filtering for the product grid.
    const priceBuckets = useMemo(() => availableBuckets(products), [products]);
    const visibleProducts = useMemo(
        () => applyCatalogFilters(products, { sortBy, priceBucket }),
        [products, sortBy, priceBucket]
    );

    // Simple ethereal animations
    const cardVariants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1
            }
        }
    };

    if (loading) {
        return (
            <div className="loading-container" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                fontSize: '1.5rem'
            }}>
                Loading...
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                fontSize: '1.5rem',
                color: 'red'
            }}>
                {error}
            </div>
        );
    }

    return (
        <>
           <Navbar/>
            <div className="product-page-container">
                <PageHeroBanner
                    heading={heroHeading}
                    image={CATEGORY_BANNER_IMAGE}
                    eyebrow={heroEyebrow}
                    description={heroDescription}
                    className="page-hero-banner--products"
                />
                <div className="product-page">
                    {products.length > 0 && (
                        <div className="cf-bar">
                            <div className="cf-group">
                                <span className="cf-label">Price</span>
                                <button className={`cf-chip ${priceBucket === 'all' ? 'cf-chip--active' : ''}`} onClick={() => setPriceBucket('all')}>All</button>
                                {priceBuckets.map((b) => (
                                    <button key={b.id} className={`cf-chip ${priceBucket === b.id ? 'cf-chip--active' : ''}`} onClick={() => setPriceBucket(b.id)}>{b.label}</button>
                                ))}
                            </div>
                            <span className="cf-spacer" />
                            <div className="cf-group">
                                <label className="cf-label" htmlFor="pp-sort">Sort</label>
                                <select id="pp-sort" className="cf-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                                    {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                                </select>
                            </div>
                            <span className="cf-count">{visibleProducts.length} {visibleProducts.length === 1 ? 'product' : 'products'}</span>
                        </div>
                    )}
                    <div className="products-container1">
                        <motion.div
                            className="products-grid1"
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            {visibleProducts.map((product) => (
                                <motion.div
                                    key={product.id}
                                    variants={cardVariants}
                                >
                                    <ProductCard cookie={product} />
                                </motion.div>
                            ))}
                            {visibleProducts.length === 0 && (
                                <p className="cf-count" style={{ padding: '20px 0' }}>No products match these filters.</p>
                            )}
                        </motion.div>
                    </div>

                    <ProductOptions currentCategory={categoryName} />
                </div>
                <CustomizedCake />
            </div>
            <Footer />
        </>
    );
}

export default ProductPage; 